import {defineStore} from 'pinia'

// Contrato da resposta nativa da API do Moodle (/login/token.php)
export interface MoodleTokenResponse {
    token?: string;
    privatetoken?: string; // Opcional, dependendo da configuração do Moodle
    error?: string;
    errorcode?: string;
    stacktrace?: string | null;
    debuginfo?: string | null;
    reproductionlink?: string | null;
}

// Contrato do retorno da nossa action para o componente Vue
export interface LoginFlowResult {
    success: boolean;
    errorcode?: string;
    message?: string;
}

// 1. O Contrato: O que esperamos receber do PHP (window.MOODLE_USER)
export interface MoodleUserPayload {
    id: number;
    username: string;
    fullname: string;
    email: string;
    avatar: string;
    lang: string;
    sesskey: string;
    wsToken: string;
    darkMode: boolean;
    isGuest: boolean;
    isAdmin: boolean;
    myCourses: any[];
    prefs: {
        drawerRightOpen: boolean;
        drawerLeftOpen: boolean;
        forcePasswordChange: boolean;
        emailBounceCount: number;
        courseSections: Record<string, CourseSectionPref>;
    };
}

interface CourseSectionPref {
    contentcollapsed: number[];
    indexcollapsed?: number[];
}

export const useUserStore = defineStore('user', {
    state: () => ({
        id: 0 as number,
        username: '',
        fullname: '',
        email: '',
        avatar: '',
        unreadConversationsCount: 0,
        unreadNotificationCount: 0,
        lang: 'pt_br',
        sesskey: '',
        wsToken: '',
        isAdmin: false,
        isGuest: true,
        darkMode: false,
        myCourses: [] as any[],
        prefs: {
            drawerLeftOpen: false,
            drawerRightOpen: false,
            forcePasswordChange: false,
            emailBounceCount: 0,
            courseSections: {} as Record<string, CourseSectionPref>
        },
        isLogging: false,
    }),

    getters: {
        isLoggedIn: (state) => {
            if (state.id > 0 && state.sesskey != '') {
                if (state.wsToken != '' || state.isAdmin) return true;
            }
            return false
        }
    },

    actions: {
        setUser(userData: MoodleUserPayload) {
            this.id = userData.id;
            this.username = userData.username;
            this.fullname = userData.fullname;
            this.email = userData.email;
            this.avatar = userData.avatar;
            this.lang = userData.lang;
            this.sesskey = userData.sesskey;
            this.isGuest = userData.isGuest;
            this.isAdmin = userData.isAdmin;
            this.darkMode = userData.darkMode;

            this.myCourses = userData.myCourses || [];

            if (userData.prefs) {
                this.prefs.drawerRightOpen = userData.prefs.drawerRightOpen;
                this.prefs.drawerLeftOpen = userData.prefs.drawerLeftOpen;
                this.prefs.forcePasswordChange = userData.prefs.forcePasswordChange;
                this.prefs.emailBounceCount = userData.prefs.emailBounceCount;
                this.prefs.courseSections = userData.prefs.courseSections || {};
            }
        },

        isSectionCollapsed(courseId: number, sectionId: number): boolean {
            const prefs = this.prefs.courseSections[courseId];
            if (!prefs || !prefs.contentcollapsed) return false;
            return prefs.contentcollapsed.includes(sectionId);
        },

        /**
         * Armazena o token WS obtido externamente.
         */
        setWsToken(token: string) {
            this.wsToken = token;

            // A ponte segura para o cache do Pinia
            this.id = 0;

            // Um cookie imune à limpeza do Moodle
            const wsCookie = useCookie<{id: number, token: string} | null>('nuxt_ws_token', {
                maxAge: 3 * 24 * 60 * 60, // 3 dias
                sameSite: 'strict', // Protege contra requisições forjadas de outros sites (CSRF)
                secure: process.env.NODE_ENV === 'production', // true em produção (HTTPS), false em dev (http://localhost)
                path: '/'
            });

            // Salvamos com id 0 indicando que está na transição de login
            wsCookie.value = { id: 0, token: token };
        },

        clearUser() {
            this.$reset();

            // Afunda o bote salva-vidas no logout
            const wsCookie = useCookie('nuxt_ws_token');
            wsCookie.value = null;
        },

        async handleFormSubmit(username: string, password: string): Promise<LoginFlowResult> {
            this.isLogging = true

            // username e password existem apenas neste escopo — nunca vão para o store.
            try {
                const config = useRuntimeConfig()
                const res = await $fetch<MoodleTokenResponse>("/login/token.php", {
                    baseURL: config.public.baseURL,
                    method: 'POST',
                    headers: {'Content-Type': 'application/x-www-form-urlencoded'},
                    body: new URLSearchParams({
                        username: username,
                        password: password,
                        service: 'app_nuxt_home',
                    }).toString(),
                })
                // Sucesso: Moodle devolveu o token
                if (res.token) {
                    this.setWsToken(res.token);
                    return { success: true };
                }

                // Falha Lógica: Moodle respondeu 200 OK, mas com objeto de erro
                console.warn('Retornou sem token.\n', res)
                return {
                    success: false,
                    errorcode: res.errorcode || 'unknown',
                    message: res.error || 'Erro desconhecido ao tentar fazer login.'
                };
            } catch (e: any) {
                // Falha de Rede/Servidor: Timeout, 500 Internal Server Error, CORS, etc.
                console.error('Falha na requisição de login HTTP.', e);
                return { success: false, errorcode: 'network_error', message: 'Erro de conexão com o servidor.' };
            } finally {
                this.isLogging = false; // Libera o estado de carregamento
            }
        },

        /**
         * Busca a contagem de notificações e mensagens não lidas no Moodle.
         * Utiliza o $fetchWS injetado globalmente pelo Nuxt.
         */
        async fetchUnreadCounts() {
            // Prevenção: só busca se já tivermos os dados necessários
            if (this.id === 0 || this.wsToken === '') {
                console.warn('Usuário não possui ID ou wsToken para buscar notificações.');
                return;
            }

            // Pega a instância fetchWS que você injetou no arquivo fetchInstances.ts
            const { $fetchWS } = useNuxtApp() as any;

            try {
                // Disparamos as duas requisições ao mesmo tempo com Promise.all para ganhar performance
                const [notifCount, convCount] = await Promise.all([
                    $fetchWS('', {
                        body: new URLSearchParams({
                            wstoken: this.wsToken,
                            wsfunction: 'core_message_get_unread_notification_count',
                            moodlewsrestformat: 'json',
                            useridto: String(this.id)
                        }).toString()
                    }),
                    $fetchWS('', {
                        body: new URLSearchParams({
                            wstoken: this.wsToken,
                            wsfunction: 'core_message_get_unread_conversations_count',
                            moodlewsrestformat: 'json',
                            useridto: String(this.id)
                        }).toString()
                    })
                ]);

                // Atualizamos o state com os valores recebidos (forçando para número)
                this.unreadNotificationCount = Number(notifCount) || 0;
                this.unreadConversationsCount = Number(convCount) || 0;

            } catch (error) {
                console.error('Falha ao buscar contagem de notificações ou mensagens', error);
            }
        },

        /**
         * Zera o contador de notificações localmente no state
         */
        resetUnreadNotificationCount() {
            this.unreadNotificationCount = 0;
            // Nota: Isso apenas zera no Front-end. Se precisar marcar como lida no Moodle,
            // você precisará de outra action usando 'core_message_mark_all_notifications_as_read'
        },

        /**
         * Zera o contador de conversas localmente no state
         */
        resetUnreadConversationsCount() {
            this.unreadConversationsCount = 0;
            // Nota: Mesma lógica acima. Se precisar marcar no Moodle,
            // busque a função 'core_message_mark_all_messages_as_read'
        }
    },

    persist: {
        // Removido o 'id' daqui. Precisamos dele no cache para garantir que
        // o wsToken salvo pertence ao usuário que está logado no momento!
        omit: ['sesskey', 'isLogging'],
        storage: {
            getItem: (key) => {
                const itemStr = window.localStorage.getItem(key)
                if (!itemStr) return null

                try {
                    const item = JSON.parse(itemStr)
                    const now = new Date()

                    // TTL expirado → limpa
                    if (now.getTime() > item.expiry) {
                        window.localStorage.removeItem(key)
                        return null
                    }

                    // @ts-ignore
                    const moodleUser = window.MOODLE_USER
                    // @ts-ignore
                    const moodleSesskey = window.MOODLE_SESSKEY || (moodleUser ? moodleUser.sesskey : '')

                    // Verifica a sessão usando a variável correta de sesskey
                    const sessaoAtiva = moodleUser && moodleUser.id > 0 && moodleSesskey !== ''
                    if (!sessaoAtiva) {
                        window.localStorage.removeItem(key)
                        return null
                    }

                    // Segurança: Se o ID salvo no cache for diferente do ID logado agora, limpa o cache.
                    // (Ignora a limpeza se o cache tiver ID 0, que é o estado transicional de login)
                    if (item.data?.id !== undefined && item.data?.id !== moodleUser.id && item.data?.id !== 0) {
                        window.localStorage.removeItem(key)
                        return null
                    }

                    return JSON.stringify(item.data)
                } catch {
                    window.localStorage.removeItem(key)
                    return null
                }
            },

            setItem: (key, value) => {
                if (import.meta.server) return;

                const now = new Date();
                const ttl = 3 * 24 * 60 * 60 * 1000; // 3 dias em milissegundos

                const item = {
                    data: JSON.parse(value),
                    expiry: now.getTime() + ttl,
                };

                window.localStorage.setItem(key, JSON.stringify(item));
            }
        }
    }
})